import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import { Mail, Plus, Edit, Loader2, AlertCircle, Check, MessageCircle, MessageSquare } from 'lucide-react';
import TemplateEditorModal from '../components/TemplateEditorModal';

const CHANNEL_LABELS = {
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
};

const channelIconMap = {
  EMAIL: Mail,
  WHATSAPP: MessageCircle,
  SMS: MessageSquare,
};

const EmailTemplatesSettings = () => {
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEventId, setSelectedEventId] = useState(searchParams.get('eventId') || '');
  const [channelFilter, setChannelFilter] = useState('ALL');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Fetch events for selection
  const { data: eventsResponse, isLoading: eventsLoading } = useQuery({
    queryKey: ['events', 'forNotificationTemplates'],
    queryFn: () => ApiService.getEvents(0, 100),
  });
  const events = Array.isArray(eventsResponse?.content) ? eventsResponse.content : [];

  // Auto-select event if only one is available
  React.useEffect(() => {
    if (!selectedEventId && events.length === 1) {
      setSelectedEventId(events.length === 1 ? events[0].id : '');
    }
  }, [events, selectedEventId]);

  // Sync selected eventId into URL so page is shareable
  React.useEffect(() => {
    const params = {};
    if (selectedEventId) {
      params.eventId = selectedEventId;
    }
    setSearchParams(params, { replace: true });
  }, [selectedEventId, setSearchParams]);

  // Fetch templates for selected event
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['notificationTemplates', selectedEventId],
    enabled: !!selectedEventId,
    queryFn: () => ApiService.getNotificationTemplates(selectedEventId),
  });

  const isLoading = eventsLoading || (!selectedEventId && eventsLoading) || templatesLoading;

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    if (channelFilter === 'ALL') return templates;
    return templates.filter((t) => t.type === channelFilter);
  }, [templates, channelFilter]);

  const handleCreateTemplate = () => {
    if (!selectedEventId) {
      showError('Please select an event before creating a template.');
      return;
    }
    setEditingTemplate(null);
    setShowEditorModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowEditorModal(true);
  };

  const handleCloseModal = () => {
    setShowEditorModal(false);
    setEditingTemplate(null);
  };

  const handleSuccess = () => {
    if (selectedEventId) {
      queryClient.invalidateQueries(['notificationTemplates', selectedEventId]);
    }
    setShowEditorModal(false);
    setEditingTemplate(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Notification Templates</h1>
            <p className="text-gray-600">
              Configure email, WhatsApp, and SMS templates for each event.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <Select
              value={selectedEventId || ''}
              onValueChange={(value) => setSelectedEventId(value)}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={channelFilter}
              onValueChange={(value) => setChannelFilter(value)}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Channels</SelectItem>
                <SelectItem value="EMAIL">Email</SelectItem>
                <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                <SelectItem value="SMS">SMS</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateTemplate} disabled={!selectedEventId}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 mb-1">Available Variables</h3>
              <p className="text-sm text-blue-800">
                You can use these variables in your templates:{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;eventName&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;userName&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;name&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;userId&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;eventDate&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;eventOrganizerName&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;email&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;password&#125;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">$&#123;registrationId&#125;</code>
              </p>
              <p className="text-sm text-blue-800 mt-2">
                HTML tags like{' '}
                <code className="bg-blue-100 px-1 rounded">&lt;strong&gt;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">&lt;br&gt;</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">&lt;a&gt;</code> are supported.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      {!selectedEventId ? (
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select an event</h3>
            <p className="text-gray-600">
              Choose an event from the dropdown above to view and manage its notification templates.
            </p>
          </CardContent>
        </Card>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
            <p className="text-gray-600 mb-6">
              Create your first notification template for this event to get started.
            </p>
            <Button onClick={handleCreateTemplate}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredTemplates.map((template) => {
            const ChannelIcon = channelIconMap[template.type] || Mail;
            return (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <ChannelIcon className="w-5 h-5 text-blue-600" />
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <Badge variant="outline" className="text-xs">
                          {CHANNEL_LABELS[template.type] || template.type}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-gray-700">
                        Subject: {template.subject}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-600 font-medium mb-2">Body Preview:</p>
                    <div
                      className="text-sm text-gray-700 whitespace-pre-wrap max-h-32 overflow-y-auto"
                      dangerouslySetInnerHTML={{
                        __html:
                          (template.body || '')
                            .replace(/\n/g, '<br/>')
                            .substring(0, 300) +
                          ((template.body || '').length > 300 ? '...' : ''),
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Template Editor Modal */}
      {showEditorModal && (
        <TemplateEditorModal
          eventId={selectedEventId}
          template={editingTemplate}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default EmailTemplatesSettings;

